import React, { useEffect, useState } from 'react';
import { HeaderBox } from './mBookListPage';
import { Box, IconButton, Typography, Paper, Button } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GeetGovindServices from "../../Services/GeetGovind";
import ListCoverImage from './Common/ListCoverImage';

export enum Product {
    Book = 1,
    Audio = 2,
    Pravachan = 4,
    Article = 3,
    Kalyan = 5,
    KalyanKalpataru = 6,
    MonthlyMagazine = 7,
    VivekVani = 8,
    Video = 9,
    DailySatsang = 10
}

const MobileMagazines = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const fixedBottomBarHeight = 56;

    const [magazines, setMagazines] = useState<any>([]);
    // Sum of per-magazine issue counts from magazineTotal on /api/magazines
    const [totalRecordsSum, setTotalRecordsSum] = useState(0);

    useEffect(() => {
        const fetchMagazines = async () => {
            const logdetails: string[] = ['fetch_magazines_start'];
            try {
                // Single catalog API already returns magazineTotal per item — no need for 4 issue-list calls
                const response = await GeetGovindServices.getAllMagazines();
                if (response.status && response.result && response.result.items) {
                    const items = response.result.items;
                    // Total issues across all magazine types (e.g. 46+44+132+300+7)
                    const issueTotal = items.reduce(
                        (sum: number, item: any) => sum + Number(item?.magazineTotal || 0),
                        0
                    );

                    setMagazines(items);
                    setTotalRecordsSum(issueTotal);

                    logdetails.push(`catalog_count=${response.result.totalRecords}`);
                    logdetails.push(`issue_total=${issueTotal}`);
                    logdetails.push('fetch_magazines_success');
                } else {
                    logdetails.push(`fetch_magazines_failed message=${response?.message}`);
                    console.error("Failed to fetch magazines:", response.message);
                }
            } catch (error) {
                logdetails.push(`fetch_magazines_error=${error}`);
                console.error("Error fetching magazines:", error);
            } finally {
                console.log('[MobileMagazines]', logdetails.join(' | '));
            }
        };

        fetchMagazines();
    }, []);

    // Catalog cards are magazine categories — open the section list, never pass catalog id
    const getLangLabel = (rawLang: any): string => {
        if (!rawLang && rawLang !== 0) return '';
        const lang = String(rawLang).toLowerCase();
        if (lang === 'english' || lang === 'en' || lang.includes('english') || lang.includes('angrezi') || lang.includes('अंग्रे')) {
            return 'english';
        }
        if (lang === 'hindi' || lang === 'hi' || lang.includes('hindi') || lang.includes('हिं') || lang.includes('हिन')) {
            return 'hindi';
        }
        return '';
    };

    const handleMagazineClick = (productType: any, magazineLanguage: any) => {
        const langLabel = getLangLabel(magazineLanguage) || 'hindi';
        const logdetails: string[] = [
            `category_click productType=${productType}`,
            `magazineLanguage=${magazineLanguage || ''}`,
            `langLabel=${langLabel}`,
        ];

        switch (productType) {
            case Product.MonthlyMagazine:
                // Geet Govind Hindi/English → magazine home (detail + carousel)
                logdetails.push(`route=/magazines/geetgovind lang=${langLabel}`);
                navigate('/magazines/geetgovind', {
                    state: {
                        type: 'geetGovind',
                        geetGovindLang: langLabel,
                        magazineLang: langLabel,
                        magazineLanguage: langLabel,
                        productType: Product.MonthlyMagazine,
                    },
                });
                break;
            case Product.Kalyan:
                logdetails.push('route=/magazines/kalyan');
                navigate('/magazines/kalyan', {
                    state: { productId: productType, magazineLanguage },
                });
                break;
            case Product.KalyanKalpataru:
                logdetails.push('route=/magazines/kalyanakalpataru');
                navigate('/magazines/kalyanakalpataru', {
                    state: { productId: productType, magazineLanguage },
                });
                break;
            case Product.VivekVani:
                logdetails.push('route=/magazines/vivekvani');
                navigate('/magazines/vivekvani', {
                    state: {
                        type: 'vivekVani',
                        vivekVaniId: Product.VivekVani,
                        magazineLanguage,
                        productType,
                    },
                });
                break;
            default:
                logdetails.push('route=unhandled');
                break;
        }
        console.log('[MobileMagazines]', logdetails.join(' | '));
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <HeaderBox>
                    <>
                        <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 16, top: 35, p: 0 }}>
                            <ArrowBackIosIcon sx={{ color: "white", fontSize: '18px' }} />
                        </IconButton>
                        <IconButton onClick={() => navigate("/")} sx={{ position: 'absolute', left: 48, top: 35, p: 0 }}>
                            <HomeOutlinedIcon sx={{ color: "white", fontSize: '24px' }} />
                        </IconButton>
                        <Typography variant="caption" sx={{ color: "#ffffffdd", opacity: 0.9, mb: 0.5 }}>
                            {t("shrihari_tr")}
                        </Typography>
                        <Typography variant="h6" sx={{ color: "#ffffffdd", opacity: 0.9, fontSize: 15 }}>
                            {t("magazine_tr")}
                        </Typography>
                    </>
                </HeaderBox>
                <div style={{ width: "100%", backgroundColor: "whitesmoke", textAlign: 'center', position: 'fixed', top: 90, zIndex: 1200, padding: '1px 0', }}>
                    {t("Total_Magazines_tr")} : {totalRecordsSum}
                </div>
                <Box
                    sx={{
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        flexGrow: 1,
                        overflowY: 'auto',
                        pb: `${fixedBottomBarHeight}px`,
                        backgroundColor: "whitesmoke",
                        mt: '28px',
                        px: 1.5,
                        pt: 1.5,
                    }}
                    role="presentation"
                >
                    {magazines?.map((magazine: any) => {
                        const productType = magazine?.productType ?? magazine?.productId;
                        const totalIssues = Number(
                            magazine?.magazineTotal ?? magazine?.total ?? magazine?.totalIssues ?? 0
                        );
                        const langLabel = magazine?.magazineLangauge || '—';
                        const latestIssue = magazine?.latest_Issue?.formattedIssue ?? '—';

                        return (
                            <Paper
                                key={magazine.id}
                                elevation={0}
                                onClick={() => handleMagazineClick(productType, magazine.magazineLangauge)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'stretch',
                                    gap: 1.5,
                                    p: 1.5,
                                    mb: 1.5,
                                    borderRadius: '16px',
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                                    cursor: 'pointer',
                                }}
                            >
                                {/* Cover — 5px rounded corners as requested */}
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        width: 96,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <ListCoverImage
                                        src={magazine.coverPageThumbPath || magazine.coverPagePath}
                                        alt={magazine.name}
                                        style={{
                                            width: '100%',
                                            height: '140px',
                                            borderRadius: '5px',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                                        }}
                                        imgStyle={{
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Box>

                                {/* Text + CTA */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'chanakyaUni',
                                                fontSize: 18,
                                                fontWeight: 700,
                                                color: '#3f220d',
                                                lineHeight: 1.25,
                                                mb: 0.25,
                                            }}
                                        >
                                            {magazine.name}
                                        </Typography>

                                        {magazine.description && (
                                            <Typography
                                                component="div"
                                                sx={{
                                                    fontFamily: 'chanakyaUni',
                                                    fontSize: 14,
                                                    color: '#5c4636',
                                                    lineHeight: 1.3,
                                                    mb: 0.75,
                                                    '& p': { m: 0 },
                                                }}
                                                dangerouslySetInnerHTML={{ __html: magazine.description }}
                                            />
                                        )}

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1.5,
                                                mb: 0.5,
                                                fontFamily: 'chanakyaUni',
                                                fontSize: 13,
                                                color: '#4a3426',
                                            }}
                                        >
                                            <Box component="span">
                                                <Box component="strong" sx={{ fontWeight: 700 }}>
                                                    {t('Language_tr')}:
                                                </Box>{' '}
                                                {langLabel}
                                            </Box>
                                            <Box component="span">
                                                <Box component="strong" sx={{ fontWeight: 700 }}>
                                                    {t('Issues_tr')}:
                                                </Box>{' '}
                                                {totalIssues}
                                            </Box>
                                        </Box>

                                        <Typography
                                            sx={{
                                                fontFamily: 'chanakyaUni',
                                                fontSize: 13,
                                                color: '#4a3426',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            <Box component="span" sx={{ fontWeight: 700 }}>
                                                {t('LatestIssue_tr', 'Latest issue')}:
                                            </Box>{' '}
                                            {latestIssue}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                        <Button
                                            size="small"
                                            endIcon={<ArrowForwardIosIcon sx={{ fontSize: '12px !important' }} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleMagazineClick(productType, magazine.magazineLangauge);
                                            }}
                                            sx={{
                                                textTransform: 'none',
                                                fontFamily: 'chanakyaUni',
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: '#C6011F',
                                                border: '1px solid #C6011F',
                                                borderRadius: '8px',
                                                px: 1.25,
                                                py: 0.25,
                                                minHeight: 30,
                                                backgroundColor: 'transparent',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(198, 1, 31, 0.06)',
                                                    border: '1px solid #C6011F',
                                                },
                                            }}
                                        >
                                            {t('magazine_view_all_tr')}
                                        </Button>
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
            </Box>
        </>
    );
};

export default MobileMagazines;
